// =============================================================
// FLOW (Phase 9.1): Admin console.
//
// Ports the prototype SecMassReplace / SecFlags / SecIP / SecAudit / SecERP
// onto the REAL /api/v1, RBAC server-enforced. Four sub-areas (tabbed):
//   - Mass replace performer  → POST /tasks/mass-replace-performer (dry-run preview)
//   - Feature flags           → GET/PUT /feature-flags (per-tenant module toggles;
//                               disabling one removes its endpoints + nav entry)
//   - Security (IP + audit)    → /ip-rules (CIDR allow/block) + /audit-log
//   - Dynamics 365 BC          → /integrations/bc/mappings + /sync
//                               (Synced/Out-of-sync/Orphaned live state;
//                                regenerate-key-terminates → Orphaned)
// =============================================================

import { useEffect, useState } from "react";
import { ApiError } from "@perpetuum/core";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import type { AuditLogFilters, IpTestResult } from "@perpetuum/core";
import {
  useAddIpRule,
  useAuditLog,
  useBcMappings,
  useCollection,
  useFeatureFlags,
  useIpRules,
  useMassReplace,
  useRemoveIpRule,
  useSetFeatureFlag,
  useTestIp,
} from "../lib/queries.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { useScopedT } from "../lib/i18n-keys.js";

type Tab = "mass" | "flags" | "security" | "bc";

export function Admin() {
  const t = useScopedT();
  const [tab, setTab] = useState<Tab>("flags");
  return (
    <div>
      <h2 className="d-h2">{t("admin.title")}</h2>
      <p className="d-sub">{t("admin.sub")}</p>

      <div className="view-seg seg" role="tablist" aria-label={t("admin.tabsLabel")} style={{ marginBottom: 18 }}>
        {(["flags", "mass", "security", "bc"] as Tab[]).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-pressed={tab === k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}>
            {t(`admin.tab.${k}`)}
          </button>
        ))}
      </div>

      {tab === "flags" && <FlagsTab />}
      {tab === "mass" && <MassReplaceTab />}
      {tab === "security" && <SecurityTab />}
      {tab === "bc" && <BcTab />}
    </div>
  );
}

const FLAG_ICONS: Record<string, string> = {
  measurements: ICONS.gauge,
  downtimes: ICONS.clock,
  guest: ICONS.users,
  counter: ICONS.refresh,
};

function FlagsTab() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const flags = useFeatureFlags();
  const setFlag = useSetFeatureFlag();
  const canWrite = can("admin", "write");

  const onToggle = async (id: string, name: string, enabled: boolean) => {
    try {
      await setFlag.mutateAsync({ moduleId: id, enabled });
      toast(t(enabled ? "admin.flag.enabled" : "admin.flag.disabled", { name }), "info");
    } catch {
      toast(t("admin.flag.toggleErr"), "error");
    }
  };

  return (
    <div className="flag-grid" data-testid="flag-grid">
      {(flags.data ?? []).map((f) => (
        <div className="flag" key={f.id} data-testid={`flag-${f.id}`}>
          <div className="ic">
            <Icon d={FLAG_ICONS[f.id] ?? ICONS.sliders} size={20} />
          </div>
          <div className="txt">
            <div className="nm">{f.name}</div>
            <div className="ds">{f.description}</div>
          </div>
          <button
            className={`toggle ${f.enabled ? "on" : ""}`}
            role="switch"
            aria-checked={f.enabled}
            aria-label={f.name}
            data-testid={`flag-toggle-${f.id}`}
            disabled={!canWrite || setFlag.isPending}
            onClick={() => void onToggle(f.id, f.name, !f.enabled)}
            style={toggleStyle(f.enabled)}
          >
            <span className="knob" />
          </button>
        </div>
      ))}
      {flags.isLoading && <div className="ad-none">{t("common.loading")}</div>}
    </div>
  );
}

const STEP_KEYS = ["admin.mass.step.current", "admin.mass.step.scope", "admin.mass.step.new", "admin.mass.step.confirm"];

function MassReplaceTab() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const users = useCollection("users");
  const locations = useCollection("locations");
  const mass = useMassReplace();
  const canWrite = can("tasks", "write");

  const [step, setStep] = useState(0);
  const [fromUserId, setFromUserId] = useState("");
  const [toUserId, setToUserId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [affected, setAffected] = useState<number | null>(null);

  // Preview the count when entering the Confirm step.
  useEffect(() => {
    if (step !== 3 || !fromUserId || !toUserId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await mass.mutateAsync({ fromUserId, toUserId, locationId: locationId || undefined, dryRun: true });
        if (!cancelled) setAffected(res.affected);
      } catch {
        if (!cancelled) setAffected(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, fromUserId, toUserId, locationId]);

  const onTransfer = async () => {
    try {
      const res = await mass.mutateAsync({ fromUserId, toUserId, locationId: locationId || undefined });
      toast(t("admin.mass.reassigned", { n: res.affected }), "success");
      setStep(0);
      setAffected(null);
    } catch {
      toast(t("admin.mass.reassignErr"), "error");
    }
  };

  const userOpts = (users.data ?? []).map((u) => ({ id: u.id, name: String(u.name) }));

  return (
    <div className="d-card">
      <div className="d-card-body">
        <div className="stepper" data-testid="mass-stepper">
          {STEP_KEYS.map((s, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span className={`step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`}>
                <span className="num">{i < step ? <Icon d={ICONS.check} size={13} sw={3} /> : i + 1}</span>
                <span className="lbl">{t(s)}</span>
              </span>
              {i < STEP_KEYS.length - 1 && <span className="step-line" />}
            </span>
          ))}
        </div>

        <div style={{ maxWidth: 560 }}>
          {step === 0 && (
            <div className="d-field">
              <label htmlFor="from-u">{t("admin.mass.step.current")}</label>
              <div className="field-ctrl">
                <select id="from-u" data-testid="mass-from" value={fromUserId} onChange={(e) => setFromUserId(e.target.value)} style={selectStyle}>
                  <option value="">{t("admin.mass.select")}</option>
                  {userOpts.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="d-field">
              <label htmlFor="scope-loc">{t("admin.mass.filterLoc")}</label>
              <div className="field-ctrl">
                <select id="scope-loc" data-testid="mass-loc" value={locationId} onChange={(e) => setLocationId(e.target.value)} style={selectStyle}>
                  <option value="">{t("admin.mass.allLocations")}</option>
                  {(locations.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {String(l.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="d-field">
              <label htmlFor="to-u">{t("admin.mass.step.new")}</label>
              <div className="field-ctrl is-active">
                <select id="to-u" data-testid="mass-to" value={toUserId} onChange={(e) => setToUserId(e.target.value)} style={selectStyle}>
                  <option value="">{t("admin.mass.select")}</option>
                  {userOpts.filter((u) => u.id !== fromUserId).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="inline-alert" data-testid="mass-confirm" style={{ background: "var(--brand-wash)", color: "var(--sys-fg-1)" }}>
              <Icon d={ICONS.users} style={{ color: "var(--brand-primary)" }} />
              <div>
                {t("admin.mass.confirmLead")} <b data-testid="mass-affected">{affected ?? "…"}</b> {t("admin.mass.confirmTrail")}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          {step > 0 && (
            <Btn variant="ghost" size="sm" data-testid="mass-back" onClick={() => setStep((s) => s - 1)}>
              {t("admin.mass.back")}
            </Btn>
          )}
          {step < 3 ? (
            <Btn
              variant="primary"
              size="sm"
              data-testid="mass-next"
              disabled={(step === 0 && !fromUserId) || (step === 2 && !toUserId)}
              onClick={() => setStep((s) => s + 1)}
            >
              {t("admin.mass.continue")}
            </Btn>
          ) : (
            <Btn variant="primary" size="sm" data-testid="mass-transfer" disabled={!canWrite || mass.isPending || !affected} onClick={() => void onTransfer()}>
              {t("admin.mass.transfer", { n: affected ?? 0 })}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

function SecurityTab() {
  const t = useScopedT();
  const { can, api } = useAuth();
  const { toast, confirm } = useChrome();
  const rules = useIpRules();
  const add = useAddIpRule();
  const remove = useRemoveIpRule();
  const testIp = useTestIp();
  const canWrite = can("admin", "write");

  const [cidr, setCidr] = useState("");
  const [testInput, setTestInput] = useState("");
  const [testResult, setTestResult] = useState<IpTestResult | null>(null);
  const blocked = (rules.data ?? []).filter((r) => r.kind === "block");
  const allowed = (rules.data ?? []).filter((r) => r.kind === "allow");

  // Audit filters (date range, actor, action) — driven server-side.
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const filters: AuditLogFilters = {
    ...(from ? { from: new Date(`${from}T00:00:00.000Z`).toISOString() } : {}),
    ...(to ? { to: new Date(`${to}T23:59:59.999Z`).toISOString() } : {}),
    ...(actorId.trim() ? { actorId: actorId.trim() } : {}),
    ...(action.trim() ? { action: action.trim() } : {}),
  };
  const audit = useAuditLog(filters);

  const onAddAllow = async () => {
    if (!cidr.trim()) return;
    try {
      await add.mutateAsync({ kind: "allow", cidr: cidr.trim(), reason: "Manual" });
      toast(t("admin.sec.whitelisted", { cidr: cidr.trim() }), "success");
      setCidr("");
    } catch (e) {
      toast(e instanceof ApiError && e.code === "invalid_cidr" ? t("admin.sec.invalidCidr") : t("admin.sec.addErr"), "error");
    }
  };

  const onUnblock = async (id: string, ip: string) => {
    const ok = await confirm({ title: t("admin.sec.unblockTitle", { ip }), body: t("admin.sec.unblockBody"), confirmLabel: t("admin.sec.unblockConfirm") });
    if (!ok) return;
    try {
      await remove.mutateAsync(id);
      toast(t("admin.sec.unblocked", { ip }), "success");
    } catch {
      toast(t("admin.sec.unblockErr"), "error");
    }
  };

  const onTestIp = async () => {
    const ip = testInput.trim();
    if (!ip) return;
    try {
      const res = await testIp.mutateAsync(ip);
      setTestResult(res);
    } catch (e) {
      setTestResult(null);
      toast(e instanceof ApiError && e.code === "invalid_ip" ? t("admin.sec.testInvalidIp") : t("admin.sec.testErr"), "error");
    }
  };

  const onExportCsv = async () => {
    try {
      const csv = await api.auditLogCsv(filters);
      // Build a client-side blob download (the auth header can't ride an <a href>).
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit-log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast(t("admin.sec.exportErr"), "error");
    }
  };

  return (
    <div className="d-grid-2">
      <div>
        <div className="d-card" style={{ marginBottom: 18 }}>
          <div className="d-card-hd">
            <h3>{t("admin.sec.blocklist")}</h3>
            <span className="sp" style={{ flex: 1 }} />
            <span className="row-tag violation" data-testid="block-count">
              {t("admin.sec.blockedCount", { n: blocked.length })}
            </span>
          </div>
          <div className="d-card-body" style={{ padding: 0 }}>
            <table className="d-table" data-testid="block-table">
              <thead>
                <tr>
                  <th>{t("admin.sec.colIp")}</th>
                  <th>{t("admin.sec.colReason")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {blocked.map((b) => (
                  <tr key={b.id} className="rowhover">
                    <td className="mono" style={{ fontWeight: 700 }}>
                      {b.cidr}
                    </td>
                    <td>{b.reason ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      {canWrite && (
                        <Btn variant="ghost" size="sm" data-testid={`unblock-${b.id}`} onClick={() => void onUnblock(b.id, b.cidr)}>
                          {t("admin.sec.unblock")}
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
                {blocked.length === 0 && (
                  <tr>
                    <td colSpan={3} className="ad-none" style={{ textAlign: "center" }}>
                      {t("admin.sec.noBlocked")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* IP test — would this address pass the current rule set? (Deliverable 4) */}
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("admin.sec.testTitle")}</h3>
            <span className="dict-hint">{t("admin.sec.testHint")}</span>
          </div>
          <div className="d-card-body">
            <div style={{ display: "flex", gap: 8 }}>
              <div className="field-ctrl" style={{ flex: 1 }}>
                <Icon d={ICONS.search} size={16} style={{ color: "var(--sys-fg-mute)" }} />
                <input
                  aria-label={t("admin.sec.testInputLabel")}
                  data-testid="ip-test-input"
                  className="mono"
                  placeholder={t("admin.sec.testPlaceholder")}
                  value={testInput}
                  onChange={(e) => {
                    setTestInput(e.target.value);
                    setTestResult(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onTestIp();
                  }}
                />
              </div>
              <Btn variant="secondary" size="sm" data-testid="ip-test-btn" disabled={!testInput.trim() || testIp.isPending} onClick={() => void onTestIp()}>
                {t("admin.sec.test")}
              </Btn>
            </div>
            {testResult && (
              <div
                className="inline-alert"
                data-testid="ip-test-result"
                style={{
                  marginTop: 12,
                  background: testResult.allowed ? "var(--status-success-wash, var(--brand-wash))" : "var(--status-danger-wash, var(--brand-wash))",
                  color: "var(--sys-fg-1)",
                }}
              >
                <Icon d={testResult.allowed ? ICONS.shield : ICONS.alert} style={{ color: testResult.allowed ? "var(--status-success)" : "var(--status-danger, var(--status-warning-ink))" }} />
                <div>
                  <b className="mono">{testResult.ip}</b> {t("admin.sec.wouldBe")}{" "}
                  <b data-testid="ip-test-verdict">{testResult.allowed ? t("admin.sec.allowed") : t("admin.sec.blocked")}</b>{" "}
                  {testResult.reason === "allowlisted" && t("admin.sec.reasonAllowlisted", { ranges: testResult.matchedAllow.join(", ") })}
                  {testResult.reason === "blocked" && t("admin.sec.reasonBlocked", { rules: testResult.matchedBlock.join(", ") })}
                  {testResult.reason === "default_allow" && t("admin.sec.reasonDefaultAllow")}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="d-card" style={{ marginBottom: 18 }}>
          <div className="d-card-hd">
            <h3>{t("admin.sec.allowedRanges")}</h3>
          </div>
          <div className="d-card-body">
            {allowed.map((r) => (
              <div className="field-ctrl" key={r.id} style={{ marginBottom: 10 }}>
                <Icon d={ICONS.shield} size={16} style={{ color: "var(--status-success)" }} />
                <input value={r.cidr} readOnly className="mono" />
                {canWrite && (
                  <button className="row-icon-btn" aria-label={t("admin.sec.removeRange", { cidr: r.cidr })} onClick={() => void remove.mutateAsync(r.id)}>
                    <Icon d={ICONS.trash} size={15} />
                  </button>
                )}
              </div>
            ))}
            {canWrite && (
              <div style={{ display: "flex", gap: 8 }}>
                <div className="field-ctrl" style={{ flex: 1 }}>
                  <input aria-label={t("admin.sec.newRangeLabel")} data-testid="cidr-input" placeholder={t("admin.sec.cidrPlaceholder")} value={cidr} onChange={(e) => setCidr(e.target.value)} />
                </div>
                <Btn variant="secondary" size="sm" data-testid="cidr-add" disabled={!cidr.trim() || add.isPending} onClick={() => void onAddAllow()}>
                  <Icon d={ICONS.plus} size={15} />
                  {t("admin.sec.addRange")}
                </Btn>
              </div>
            )}
          </div>
        </div>

        {/* Audit trail — filters + full table + CSV export (Deliverable 3) */}
        <div className="d-card">
          <div className="d-card-hd">
            <h3>{t("admin.sec.auditTrail")}</h3>
            <span className="sp" style={{ flex: 1 }} />
            <Btn variant="ghost" size="sm" data-testid="audit-export" onClick={() => void onExportCsv()}>
              <Icon d={ICONS.file} size={15} />
              {t("admin.sec.exportCsv")}
            </Btn>
          </div>
          <div className="d-card-body">
            <div className="audit-filters" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "var(--sys-fg-mute)" }}>
                {t("admin.sec.filterFrom")}
                <input type="date" data-testid="audit-from" value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "var(--sys-fg-mute)" }}>
                {t("admin.sec.filterTo")}
                <input type="date" data-testid="audit-to" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "var(--sys-fg-mute)", flex: 1, minWidth: 120 }}>
                {t("admin.sec.filterActor")}
                <input data-testid="audit-actor" placeholder={t("admin.sec.filterActorPlaceholder")} value={actorId} onChange={(e) => setActorId(e.target.value)} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "var(--sys-fg-mute)", flex: 1, minWidth: 120 }}>
                {t("admin.sec.filterAction")}
                <input data-testid="audit-action" placeholder={t("admin.sec.filterActionPlaceholder")} value={action} onChange={(e) => setAction(e.target.value)} />
              </label>
            </div>

            {/* The audit_log schema stores actor/action/target/ip/time only — it has
                NO before/after (old→new) value columns — so we render exactly what
                exists and note the absence rather than fabricating a value diff. */}
            <p className="dict-hint" data-testid="audit-no-diff" style={{ margin: "0 0 8px" }}>
              {t("admin.sec.noDiff")}
            </p>

            <table className="d-table" data-testid="audit-table">
              <thead>
                <tr>
                  <th>{t("admin.sec.colTime")}</th>
                  <th>{t("admin.sec.colActor")}</th>
                  <th>{t("admin.sec.colTarget")}</th>
                  <th>{t("admin.sec.colIpShort")}</th>
                  <th>{t("admin.sec.colAction")}</th>
                </tr>
              </thead>
              <tbody>
                {audit.isLoading && (
                  <tr>
                    <td colSpan={5} className="ad-none" style={{ textAlign: "center" }}>
                      {t("common.loading")}
                    </td>
                  </tr>
                )}
                {!audit.isLoading &&
                  (audit.data ?? []).map((a) => (
                    <tr key={a.id} className="rowhover">
                      <td className="mono" style={{ whiteSpace: "nowrap" }}>
                        {a.createdAt ? new Date(a.createdAt).toLocaleString() : "—"}
                      </td>
                      <td style={{ fontWeight: 600 }}>{a.actorName ?? "—"}</td>
                      <td className="mono">{a.target ?? "—"}</td>
                      <td className="mono" style={{ color: "var(--sys-fg-3)" }}>{a.ip ?? "—"}</td>
                      <td>
                        {a.action}
                        {a.fromMobile && <span className="tag-mobile">{t("admin.sec.tagMobile")}</span>}
                      </td>
                    </tr>
                  ))}
                {!audit.isLoading && (audit.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="ad-none" style={{ textAlign: "center" }} data-testid="audit-empty">
                      {t("admin.sec.noAudit")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function BcTab() {
  const t = useScopedT();
  const mappings = useBcMappings();
  const data = mappings.data ?? [];
  const counts = {
    synced: data.filter((m) => m.state === "synced").length,
    out_of_sync: data.filter((m) => m.state === "out_of_sync").length,
    orphaned: data.filter((m) => m.state === "orphaned").length,
  };

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{t("admin.bc.title")}</h3>
        <span className="sp" style={{ flex: 1 }} />
        <span className="sync-dot synced" data-testid="bc-count-synced">
          <span className="led" />
          {counts.synced}
        </span>
        <span className="sync-dot out_of_sync" style={{ marginLeft: 10 }} data-testid="bc-count-out">
          <span className="led" />
          {counts.out_of_sync}
        </span>
        <span className="sync-dot orphaned" style={{ marginLeft: 10 }} data-testid="bc-count-orphan">
          <span className="led" />
          {counts.orphaned}
        </span>
      </div>
      <div className="d-card-body" style={{ padding: 0 }}>
        <p className="d-sub" style={{ padding: "12px 18px 0", margin: 0 }}>
          {t("admin.bc.note")}
        </p>
        <table className="d-table" data-testid="bc-table">
          <thead>
            <tr>
              <th>{t("admin.bc.colSpare")}</th>
              <th>{t("admin.bc.colProductId")}</th>
              <th>{t("admin.bc.colStatus")}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((m) => (
              <tr key={m.id} className="rowhover">
                <td className="mono">{m.spareId.slice(0, 8)}…</td>
                <td>
                  <span className="ro-badge">{m.bcItemNo ?? "—"}</span>
                </td>
                <td>
                  <span className={`sync-dot ${m.state}`} data-testid={`bc-state-${m.id}`}>
                    <span className="led" />
                    {t(`admin.bc.state.${m.state}`)}
                  </span>
                </td>
              </tr>
            ))}
            {data.length === 0 && (
              <tr>
                <td colSpan={3} className="ad-none" style={{ textAlign: "center" }} data-testid="bc-empty">
                  {t("admin.bc.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const selectStyle = {
  border: 0,
  background: "none",
  outline: "none",
  font: "inherit",
  color: "inherit",
  width: "100%",
} as const;

function toggleStyle(on: boolean) {
  return {
    width: 38,
    height: 22,
    borderRadius: 999,
    border: 0,
    cursor: "pointer",
    padding: 2,
    background: on ? "var(--brand-primary-fill)" : "var(--sys-bg-sunken)",
    display: "inline-flex",
    justifyContent: on ? "flex-end" : "flex-start",
    alignItems: "center",
    transition: "background .15s",
  } as const;
}
